import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useAuthentication } from '@src/context/hooks';
import type { Token2FAEnrollment } from '@src/corelib/auth/providers';
import { Form, Submit, TextField } from '@src/corelib/forms/generic';
import { createSchema, Yup } from '@src/corelib/validation';
import { Link, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import AuthLayout from '@src/pages/Auth/AuthLayout.jsx';

type TwoFactorEnableSearch = {
  token?: string;
  redirect?: string;
};

type TwoFactorValues = {
  otp_code: string;
};

const StyleSheet = {
  qrCode: {
    width: '220px',
    height: '220px',
    alignSelf: 'center',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
  },
  secret: {
    wordBreak: 'break-word',
  },
  links: {
    display: 'flex',
    justifyContent: 'center',
    '& a': (theme: Theme) => ({
      color: theme.palette.common.black,
      textDecoration: 'none',
    }),
  },
};

const twoFactorSchema = createSchema({
  otp_code: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Enter a valid 6-digit code'),
});

const TWO_FACTOR_DEFAULT_VALUES = { otp_code: '' };

function TwoFactorEnable() {
  const auth = useAuthentication();
  const router = useRouter();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as TwoFactorEnableSearch;
  const token = search.token ?? '';
  const redirect = search.redirect ?? '/dashboard';
  const [challenge, setChallenge] = useState<Token2FAEnrollment | null>(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!token) {
      setLoadError('Enrollment token is missing.');
      return;
    }

    let mounted = true;
    auth
      .startTwoFactorEnrollment(token)
      .then((data) => {
        if (!mounted) {
          return;
        }
        setChallenge(data);
        setLoadError('');
      })
      .catch(() => {
        if (!mounted) {
          return;
        }
      setLoadError('Enrollment link is invalid or expired.');
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  const completeLogin = () =>
    router.invalidate().finally(() => {
      navigate({ to: decodeURIComponent(redirect) as '/' });
    });

  const verifyTwoFactor = (values: Record<string, unknown>) => {
    const payload: TwoFactorValues = {
      otp_code: String(values.otp_code ?? ''),
    };

    if (!challenge?.challenge_token) {
      return Promise.reject(new Error('Challenge is missing'));
    }
    return new Promise<void>((resolve, reject) => {
      auth
        .verifyTwoFactor(challenge.challenge_token, payload.otp_code)
        .then(() => {
          completeLogin().finally(resolve);
        })
        .catch(reject);
    });
  };

  return (
    <AuthLayout>
      <Typography variant="h4" align="center">
        Enable Two-Factor Authentication
      </Typography>

      {loadError && (
        <Typography variant="body2" align="center" color="error.main">
          {loadError}
        </Typography>
      )}

      {challenge && (
        <>
          <Typography variant="body2" align="center">
            Scan this QR code in your authenticator app or use the manual key.
          </Typography>
          <Box
            component="img"
            alt="Authenticator QR"
            src={challenge.qr_code_data_url}
            sx={StyleSheet.qrCode}
          />
          <Typography variant="caption" align="center" sx={StyleSheet.secret}>
            Manual key: {challenge.manual_entry_key}
          </Typography>

          <Form
            schema={twoFactorSchema}
            defaultValues={TWO_FACTOR_DEFAULT_VALUES}
            sx={{ width: '100%' }}
            onSubmit={verifyTwoFactor}
          >
            <TextField
              required
              fullWidth
              name="otp_code"
              label="Verification code"
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
            />
            <Submit fullWidth variant="contained">
              Verify and continue
            </Submit>
          </Form>
        </>
      )}

      <Box sx={StyleSheet.links}>
        <Link to="/auth/login">Back to login</Link>
      </Box>
    </AuthLayout>
  );
}

TwoFactorEnable.displayName = 'TwoFactorEnable';
export default TwoFactorEnable;
